import { useQuery } from '@tanstack/react-query';

import { useWalletAddress } from './useWalletAddress';

import { useAuthContext } from '@/context/AuthContext';
import * as referralApi from '@/api/referral';

export interface UserPoints {
  self_point: number;
  invitee_point: number;
  backpack_bonus_point: number;
  solflare_bonus_point: number;
  total_point: number;
}

/**
 * Hook to fetch user's points from the backend
 * Returns self_point (points from own chest opens) and total_point
 *
 * This mimics the behavior of useUserProgress for SOL balance
 */
export function useUserPoints() {
  const { address, isConnected } = useWalletAddress();
  const { isAuthenticated } = useAuthContext();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userPoints', address],
    queryFn: async (): Promise<UserPoints> => {
      try {
        const response = await referralApi.getDashboard();

        if (response.code !== 0) {
          console.error('Dashboard API error:', response.message);
          // Return default values instead of throwing
          return {
            self_point: 0,
            invitee_point: 0,
            backpack_bonus_point: 0,
            solflare_bonus_point: 0,
            total_point: 0,
          };
        }

        return {
          self_point: Number(response.data.self_point) || 0,
          invitee_point: Number(response.data.invitee_point) || 0,
          backpack_bonus_point: Number(response.data.backpack_bonus_point) || 0,
          solflare_bonus_point: Number(response.data.solflare_bonus_point) || 0,
          total_point: Number(response.data.total_point) || 0,
        };
      } catch (err) {
        console.error('Failed to fetch user points:', err);
        // Return default values instead of throwing
        return {
          self_point: 0,
          invitee_point: 0,
          backpack_bonus_point: 0,
          solflare_bonus_point: 0,
          total_point: 0,
        };
      }
    },
    enabled: isConnected && isAuthenticated,
    staleTime: 0, // Always consider data stale to ensure fresh points after actions
    refetchOnWindowFocus: true,
    refetchOnMount: true, // Always refetch when component mounts
    // Don't refetch on interval to prevent constant updates
    refetchInterval: false,
    // Keep previous data to prevent disappearing during refetch
    placeholderData: (previousData) => previousData,
  });

  return {
    userPoints: data || { self_point: 0, invitee_point: 0, backpack_bonus_point: 0, solflare_bonus_point: 0, total_point: 0 },
    isLoading,
    error,
    refetch,
  };
}
